import React, { useEffect, useRef } from 'react'
import { Vehicle } from '../../entities/vehicle/Vehicle'
import Map from '../map/Map'
import Marker from '../map/marker/Marker'

export interface IOwnProps { }
export interface IStateProps {
  storeName: string,
  vehicle: Vehicle
}
export interface IDispatchProps { }
export type IProps = IOwnProps & IStateProps & IDispatchProps

const driverIcon = {
  path: 'M11.2 39.95q-2.45 0-4.175-1.7-1.725-1.7-1.725-4.2H2V11q0-1.2.9-2.1Q3.8 8 5 8h28.95v8.35h5.25L46 25.4v8.65h-3.55q0 2.5-1.725 4.2-1.725 1.7-4.175 1.7-2.45 0-4.175-1.7-1.725-1.7-1.725-4.2H17.1q0 2.5-1.725 4.2-1.725 1.7-4.175 1.7Zm0-3q1.2 0 2.05-.85.85-.85.85-2.05 0-1.2-.85-2.05-.85-.85-2.05-.85-1.2 0-2.05.85-.85.85-.85 2.05 0 1.2.85 2.05.85.85 2.05.85Zm25.35 0q1.2 0 2.05-.85.85-.85.85-2.05 0-1.2-.85-2.05-.85-.85-2.05-.85-1.2 0-2.05.85-.85.85-.85 2.05 0 1.2.85 2.05.85.85 2.05.85Zm-2.6-10.2h9.3l-5.55-7.4h-3.75Z',
  fillColor: '#008000',
  fillOpacity: 0.8,
  strokeWeight: 2,
  strokeColor: '#5F6368',
}

const storeIcon = {
  path: 'M8.25 11.35v-3h31.6v3ZM8.5 39.7V27.45H6.05v-3l2.2-10.1H39.8l2.2 10.1v3h-2.45V39.7h-3V27.45H27.6V39.7Zm3-3h13.1v-9.25H11.5Z',
  fillColor: '#FF0000',
  fillOpacity: 0.8,
  strokeWeight: 1,
  strokeColor: '#5F6368',
}

const MapWrapper: React.FC<IProps> = ({ vehicle, storeName }) => {
  const mapRef = useRef<google.maps.Map | null>(null)
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)

  // lazily create InfoWindow once
  useEffect(() => {
    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow()
    }
  }, [])

  const calculateCenter = () => {
    const locations = []
    if (vehicle.geolocation) locations.push(vehicle.geolocation)
    if (vehicle.vendorGeolocation) locations.push(vehicle.vendorGeolocation)
    if (locations.length === 0) return undefined
    if (locations.length === 1) return locations[0]
    const totalLat = locations.reduce((s, l) => s + l.lat, 0)
    const totalLng = locations.reduce((s, l) => s + l.lng, 0)
    return { lat: totalLat / locations.length, lng: totalLng / locations.length }
  }

  const fitMapToMarkers = () => {
    const map = mapRef.current
    if (!map) return

    const bounds = new google.maps.LatLngBounds()
    if (vehicle.geolocation) {
      bounds.extend(new google.maps.LatLng(vehicle.geolocation.lat, vehicle.geolocation.lng))
    }
    if (vehicle.vendorGeolocation) {
      bounds.extend(new google.maps.LatLng(vehicle.vendorGeolocation.lat, vehicle.vendorGeolocation.lng))
    }
    if ((bounds as any).isEmpty?.() || bounds.equals(new google.maps.LatLngBounds())) return

    map.fitBounds(bounds)

    setTimeout(() => {
      const currentMap = mapRef.current
      if (!currentMap) return
      const zoom = currentMap.getZoom() ?? 0
      if (zoom > 15) currentMap.setZoom(15)
    }, 100)
  }

  const center = calculateCenter()

  return (
    <Map
      center={center}
      zoom={12}
      style={{ flexGrow: '1', height: '70vh' }}
      onMapReady={(map) => {
        mapRef.current = map
        setTimeout(fitMapToMarkers, 100)
      }}
    >
      {vehicle.geolocation && (
        <Marker
          position={{ ...vehicle.geolocation }}
          icon={driverIcon}
          title={`Driver: ${vehicle.driverName}`}
          infoWindow={infoWindowRef.current ?? undefined}
        />
      )}
      {vehicle.vendorGeolocation && (
        <Marker
          position={{ ...vehicle.vendorGeolocation }}
          icon={storeIcon}
          title={`Store: ${storeName}`}
          infoWindow={infoWindowRef.current ?? undefined}
        />
      )}
    </Map>
  )
}

export default MapWrapper
